import React, { useEffect, useRef, useState } from 'react';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { NavInlineContext } from './navLayoutContext';

// ---------------------------------------------------------------------------
// TopNavBar
// ---------------------------------------------------------------------------
// Full-width horizontal app-shell nav bar (OPERA Cloud-style mega-menu
// chrome). This is a pure layout/chrome primitive: it owns the bar's
// background color, height, and the three-slot layout, but knows nothing
// about actual navigation items or user data. Phase 2's Dashboard.js
// rewrite is responsible for composing this with real `NavDropdown` /
// `UserMenu` instances and real brand/logo content.
//
// Layout: three slots across the bar -
//   - `brand` (left): logo/product name.
//   - `children` (center-left, grows to fill remaining space): the
//     mega-menu category items, e.g. a row of <NavDropdown /> elements.
//   - `right` (right-aligned): user identity + logout, e.g. <UserMenu />.
//
// Uses the `shell` color tokens (tailwind.config.js) for its background -
// NOT the landing page's blue-600 - see the palette comment there for why.
//
// Hamburger menu (2026-10-07, owner ask: "those logging in on mobile are
// really struggling" to see the full menu list, "lack zoom out abilities").
// A superuser's `children` row can run to 10+ items (Dashboard, Front
// Desk, POS, Inventory, Recipes, Kitchen Display, Shift Report, Stock
// Issues, Portion Stock, Kitchen Sheet, Daily Sales & Collection,
// Settings, Chat, Management...) — this bar's own earlier header comment
// already documented the real cause: overflow-x-auto had to be traded
// away (it clips NavDropdown's own position:absolute panels per the CSS
// overflow spec), so on a phone-width screen those items just visually
// overflow/clip with nothing to collapse into. Below the `md` breakpoint
// (768px — Tailwind's own default, comfortably above a phone, below a
// small tablet), `children` is hidden from the bar itself and a
// hamburger button appears instead; tapping it opens a full-width panel
// BELOW the bar holding the exact same children, stacked vertically full-
// width instead of in a horizontal row. No change needed to any
// individual nav item (button or NavDropdown) in Dashboard.js — they're
// passed through completely unchanged; NavDropdown's own panel is
// already position:absolute, so it still opens correctly (just below a
// wider trigger row) when used inside this vertical layout.
//
// 2026-10-09 — "the hamburger is not working on mobile". Four real faults:
//  1. The panel closed itself whenever `children` changed identity.
//     `children` is a fresh array on EVERY Dashboard render, so any refresh
//     behind the menu (a poll, a capability/entitlement load, a panel focus)
//     snapped it shut — and unmounted whichever sub-menu was open. It now
//     closes only when a destination is actually chosen.
//  2. Tapping the hamburger to CLOSE it did nothing: the outside-click
//     listener closed it on pointer-down, then the button's own click
//     toggled it straight back open. The button is now excluded from it.
//  3. Sub-menus (Front Desk, POS, Management…) opened as floating boxes
//     inside the scrolling panel, on top of the items below. They now open
//     in the flow, as an accordion (see navLayoutContext.js).
//  4. The bar only switched to the hamburger below 768px. A phone held
//     sideways (844px+) or Chrome's "Desktop site" mode (~980px) got the
//     desktop row — cramped, clipped, and with no hamburger at all. The
//     switch is now at 1024px, the same breakpoint PanelDesktop uses to
//     turn the floating-window chrome on.
function TopNavBar({ brand, right, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  // Close on a tap/click outside the open panel AND outside the hamburger
  // button (the button toggles itself). pointerdown covers touch and mouse.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    function handlePointerDown(event) {
      const t = event.target;
      if (panelRef.current && panelRef.current.contains(t)) return;
      if (buttonRef.current && buttonRef.current.contains(t)) return;
      setMobileOpen(false);
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileOpen]);

  // Picking a destination closes the menu; opening/closing a sub-menu (a
  // button with aria-haspopup) does not. NavDropdown's item buttons and the
  // plain nav buttons carry no aria-haspopup, so they close it.
  const handlePanelClick = (event) => {
    const button = event.target.closest && event.target.closest('button');
    if (!button || button.getAttribute('aria-haspopup') === 'true') return;
    setMobileOpen(false);
  };

  return (
    // no-print (src/index.css): the app shell's nav bar must never appear
    // on a printed page — it's a SIBLING of whatever page component
    // triggers window.print() in Dashboard.js's render tree, not a
    // descendant of it, so this has to be hidden globally rather than by
    // any one page's own print handling.
    <nav className="no-print w-full flex-shrink-0 bg-shell text-shell-text shadow-md relative">
      <div className="mx-auto flex h-14 max-w-full items-center gap-3 px-4">
        {brand && <div className="flex flex-shrink-0 items-center">{brand}</div>}

        {/* overflow-visible (not overflow-x-auto): NavDropdown panels are
            position:absolute and open below the h-14 bar. Per the CSS
            overflow spec, setting overflow-x to anything but visible forces
            overflow-y to auto too - so overflow-x-auto here was silently
            clipping every open dropdown panel instead of scrolling the row.
            Traded away: horizontal scroll-on-overflow for narrow screens
            with many nav items. Working dropdowns matter more than that.
            Hidden below md and replaced by the hamburger panel below —
            see this component's own header comment. */}
        <div className="hidden lg:flex flex-1 items-center gap-0.5 overflow-visible min-[1800px]:whitespace-nowrap">{children}</div>

        {/* Hamburger trigger — md:hidden mirrors the children row's own
            hidden/md:flex split exactly, so exactly one of "the row" or
            "the button" is ever visible at a given width. flex-1 here
            (only on narrow screens, since the row above owns flex-1 on
            md+) pushes `right` to the far edge the same way the row does
            on desktop. */}
        <div className="flex lg:hidden flex-1 items-center">
          <button
            type="button"
            ref={buttonRef}
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-haspopup="true"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            className="flex items-center justify-center w-10 h-10 rounded text-shell-text hover:bg-shell-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-shell transition-colors"
          >
            {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        <ThemeToggle />
        {right && <div className="flex flex-shrink-0 items-center">{right}</div>}
      </div>

      {/* Mobile menu panel — the same `children` the desktop row renders,
          stacked vertically full-width instead of in a horizontal row.
          position:absolute + max-h + overflow-y-auto so a long list
          (superuser) scrolls within the panel rather than pushing page
          content down or running off the bottom of a short phone screen. */}
      {mobileOpen && (
        <div
          ref={panelRef}
          role="menu"
          onClick={handlePanelClick}
          className="lg:hidden absolute left-0 right-0 top-full z-50 max-h-[calc(100dvh-3.5rem)] overflow-y-auto overscroll-contain bg-shell border-t border-shell-dark shadow-lg"
        >
          <NavInlineContext.Provider value={true}>
            <div className="flex flex-col py-1 px-2 gap-0.5">{children}</div>
          </NavInlineContext.Provider>
        </div>
      )}
    </nav>
  );
}

export default TopNavBar;
